/**
 * Accepted uploads, keyed by extension.
 *
 * Keyed by extension rather than MIME because `file.type` is not dependable:
 * a .csv is reported as text/csv, application/vnd.ms-excel, or '' depending on
 * the OS and what's installed, so a MIME allow-list rejects valid files on some
 * machines and can't tell a .csv from a .xls. The extension picks the canonical
 * MIME we send, which is what lets the agent keep a clean server-side list.
 */
export const UPLOAD_TYPES = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.pdf': 'application/pdf',
  '.csv': 'text/csv',
  '.txt': 'text/plain',
  '.md': 'text/plain',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
};

/**
 * Refused, but with a way forward. The agent can't read these, and a dead end
 * costs the customer the upload where "Save As" costs them one step.
 */
export const UPLOAD_GUIDANCE = {
  '.xls': 'Save this file as .xlsx or .csv and upload it again.',
  '.doc': 'Save this file as a PDF and upload it again.',
  '.docx': 'Save this file as a PDF and upload it again.'
};

/**
 * The `accept` attribute: extensions AND MIME types, so native pickers filter
 * sensibly whichever one they honour.
 */
export const ACCEPTED_FILE_TYPES = [
  ...Object.keys(UPLOAD_TYPES),
  ...new Set(Object.values(UPLOAD_TYPES))
].join(',');

/**
 * The extension of a filename, lowercased and including the dot. '' when there
 * is none — an extensionless file can't be resolved to a canonical MIME.
 */
export const getFileExtension = (fileName = '') => {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(dot).toLowerCase() : '';
};

/**
 * The MIME to send for a file, or null when we don't accept it.
 */
export const resolveMimeType = (fileName) => UPLOAD_TYPES[getFileExtension(fileName)] || null;

/**
 * The error to show for a file we won't accept — specific when we know the
 * format and how to convert it, generic otherwise. Derived from the tables
 * above so the copy can't drift from what's actually accepted.
 */
export const getUnsupportedFileError = (fileName) => {
  const guidance = UPLOAD_GUIDANCE[getFileExtension(fileName)];
  if (guidance) return `That file type isn't supported. ${guidance}`;
  return 'Invalid file type. Please upload a JPEG, PNG, WebP, PDF, CSV, Excel (.xlsx), or text file.';
};

/**
 * Maximum allowed reference file size, in megabytes.
 */
export const MAX_FILE_SIZE_MB = 10;

/**
 * Maximum allowed reference file size, in bytes.
 */
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Maximum number of files a customer can stage per upload. Kept at 1 since
 * the list agent can only ingest one file per request; the staging logic
 * already generalizes, so raising this is a one-line change.
 */
export const MAX_FILES = 1;

/**
 * Upload states for the reference image upload flow.
 */
export const UPLOAD_STATES = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  COMPLETE: 'complete',
  ERROR: 'error'
};

/**
 * Machine-readable reasons a selection can be rejected. Keeping this
 * distinct from the rendered error copy lets callers (or a future revision
 * of the copy itself) branch on the reason without parsing message text.
 */
export const REJECTION_REASONS = {
  INVALID_FILE_TYPE: 'invalid_file_type',
  FILE_TOO_LARGE: 'file_too_large',
  FILE_COUNT_EXCEEDED: 'file_count_exceeded'
};

/**
 * Reads a file and converts it to a base64 string (without the data URI prefix).
 *
 * The MIME comes from the extension, not `file.type` — see UPLOAD_TYPES. The
 * browser's own value would send application/vnd.ms-excel for a Windows .csv,
 * which the agent rejects as an .xls.
 *
 * @param {File} file - The file to convert
 * @returns {Promise<{base64: string, mimeType: string}>}
 */
export const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // result is "data:<mime>;base64,<data>" — strip the prefix
      const dataUrl = reader.result;
      const base64 = dataUrl.split(',')[1];
      resolve({ base64, mimeType: resolveMimeType(file.name) || file.type });
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Analytics labels for each resolved inputType. Anything unmapped is plain text.
 */
export const INPUT_TYPE_ANALYTICS_LABELS = {
  VOICE_TO_TEXT: 'speech to text',
  IMAGE: 'image',
  IMAGE_AND_TEXT: 'image and text',
  IMAGE_AND_VOICE: 'image and speech to text'
};

/**
 * Resolve the inputType sent to the list agent.
 *
 * Image modes take precedence over text/voice. An image paired with text
 * distinguishes dictated (IMAGE_AND_VOICE) from typed (IMAGE_AND_TEXT) so the
 * list agent gets the right instructions. Voice-to-text is the only mode that
 * survives a no-image generate — a stale image-based value from a prior
 * generate must not leak in.
 */
export const resolveInputType = ({ inputType, hasImage, hasPrompt }) => {
  if (!hasImage) return inputType === 'VOICE_TO_TEXT' ? 'VOICE_TO_TEXT' : 'TEXT';
  if (!hasPrompt) return 'IMAGE';
  return inputType === 'VOICE_TO_TEXT' ? 'IMAGE_AND_VOICE' : 'IMAGE_AND_TEXT';
};

/**
 * Build the templateData array for an `interactWithAgent` generate request.
 *
 * Pass `referenceImage: null` to omit the image keys — the auto-trigger path
 * has no image to send.
 */
export const buildTemplateData = ({
  existingMaterials,
  name,
  questionerEnabled,
  referenceImage
}) => [
  { key: 'materialsGroup', value: JSON.stringify(existingMaterials || []) },
  { key: 'title', value: name },
  ...(questionerEnabled ? [{ key: 'bathroomQuestionsEnabled', value: 'true' }] : []),
  ...(referenceImage ? [
    { key: 'referenceImageBase64', value: referenceImage.base64 },
    { key: 'referenceImageMimeType', value: referenceImage.mimeType },
    // The agent labels extracted spreadsheet/CSV text with this; "quote.csv"
    // tells the model more about the file than its MIME type does.
    { key: 'referenceImageFileName', value: referenceImage.fileName || '' }
  ] : [])
];
